import {
  actionStoreSelector,
  useActionStore,
} from '@repo/editor/src/stores/actionStore/actionStore';
import RemoteDataStatus from '@repo/editor/src/types/RemoteDataStatus';
import isAfter from 'date-fns/isAfter';
import { StateCreator } from 'zustand';

import { LOCAL_STORAGE_BANNER_SEEN_PREFIX } from 'global/constants';

import { announcementBanner } from './config';
import { State } from './types';

export const bannerStoreCreator: StateCreator<State> = (set, get) => ({
  /**
   * modal state
   * controls whether the banner is visible
   */
  isVisible: false,

  /**
   * stores banner item
   */
  item: null,

  /**
   * stores banner item
   */
  getItem: null,

  /**
   * initialize the store based on the user
   */
  init: async (user, growthBook, currentPage): Promise<void> => {
    const now = new Date();
    const bannerIsValid =
      isAfter(now, announcementBanner.starts) &&
      isAfter(announcementBanner.ends, now) &&
      (announcementBanner.feature
        ? growthBook?.isOn(announcementBanner.feature)
        : true) &&
      (announcementBanner.page
        ? announcementBanner.page === currentPage // if page is set, show banner only on specified page
        : true);

    if ((announcementBanner.feature && !user) || !bannerIsValid) {
      set({ isVisible: false });
      return;
    }

    const setItem =
      'item' in announcementBanner
        ? { item: announcementBanner.item }
        : { getItem: announcementBanner.getItem };

    set(setItem);

    // Default to localstorage for logged out users
    // If the feature is passed - should not show the banner for unauthenticated users (marked as read)
    let bannerRead: boolean | undefined =
      localStorage.getItem(
        `${LOCAL_STORAGE_BANNER_SEEN_PREFIX}:${announcementBanner.id}`
      ) === 'true';

    let existingUser = false;

    if (user) {
      await useActionStore.getState().init();

      if (useActionStore.getState().status !== RemoteDataStatus.Done) {
        return;
      }

      existingUser = isAfter(
        announcementBanner.starts,
        new Date(user.createdAt)
      );

      bannerRead = actionStoreSelector
        .actionIds(useActionStore.getState())
        ?.includes(announcementBanner.id);
    }

    set({
      isVisible:
        !bannerRead &&
        (announcementBanner.visibleFor === 'allUsers' || existingUser),
    });
  },

  close: async (): Promise<void> => {
    if (!get().isVisible) {
      return;
    }

    set({ isVisible: false });

    localStorage.setItem(
      `${LOCAL_STORAGE_BANNER_SEEN_PREFIX}:${announcementBanner.id}`,
      'true'
    );

    if (
      useActionStore
        .getState()
        .actions?.find((action) => action.id === announcementBanner.id)
    ) {
      return;
    }

    await useActionStore.getState().createAction(announcementBanner.id);
  },
});
